import { ArrowDown } from '@ui/components/icons';
import { WalletAddress } from '@ui/components/WalletAddress';
import { useWalletModal } from '@ui/components/WalletModal/useWalletModal';
import { getChainInfo } from '@ui/constants/chain';
import { useOneClickTradingStore } from '@ui/features/OneClickTrading/store/useOneClickTradingStore';
import { useIsWrongNetwork } from '@ui/hooks/datachain';
import { useSwitchChain } from '@ui/hooks/useSwitchChain';
import { NotificationDot } from '@ui/layouts/components/NotificationDot';
import { Button, ButtonProps } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Menu } from '@ui/lib/park/ui/menu';
import { useTranslation } from 'next-i18next';
import { Box, Divider, styled } from 'styled-system/jsx';
import { useAccount, useChainId } from 'wagmi';

import { useCurrentConnector } from '../hooks/useCurrentConnector';
import { NetworkSwitchModal } from './NetworkSwitchModal';
import { UserWalletMenu } from './UserWalletMenu';

export const WalletConnectButton = (p: ButtonProps) => {
  const { open } = useWalletModal();

  return (
    <>
      <ConnectButton
        {...p}
        onClickConnect={() => {
          open();
        }}
      />
    </>
  );
};

export const ConnectButton = ({
  onClickConnect,
  ...p
}: ButtonProps & {
  onClickConnect: () => void;
}) => {
  const { isConnected } = useAccount();
  const isWrongNetwork = useIsWrongNetwork();
  const { isPending, switchChain } = useSwitchChain();
  const { t } = useTranslation();

  if (isWrongNetwork) {
    return (
      <NetworkSwitchModal
        title={t('common.Incompatible Network')}
        switchChain={(c) => switchChain(c)}
      >
        <Button
          variant="solid"
          colorPalette="priceDown"
          loading={isPending}
          {...p}
        >
          {t('common.Wrong Network')}
        </Button>
      </NetworkSwitchModal>
    );
  }

  if (isConnected) {
    return <Connected />;
  }

  return (
    <Button
      variant="solid"
      {...p}
      colorPalette="buttonPrimary"
      onClick={onClickConnect}
      data-cy="ConnectButton"
    >
      {p.children ? p.children : t('common.Connect')}
    </Button>
  );
};

const Connected = () => {
  const currentConnector = useCurrentConnector();
  const chainId = useChainId();
  const { address } = useAccount();
  const isOneClickEnabled = useOneClickTradingStore((s) => s.enabled);
  const hasCheckedOneClickStatus = useOneClickTradingStore(
    (s) => s.hasCheckedStatus,
  );

  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        <UserWalletMenu>
          <Box
            bg="v2.bg.surface1"
            color="greyWhite.lightGrey1"
            border="1px solid"
            borderColor="v2.border.default"
            borderRadius="2px"
            px="10px"
            py="6px"
            display="inline-flex"
            alignItems="center"
            gap="7px"
            cursor="pointer"
            h="8"
            transition="border-color 120ms"
            _hover={{
              borderColor: 'v2.border.strong',
            }}
          >
            {address ? (
              <>
                {hasCheckedOneClickStatus && isOneClickEnabled ? (
                  <styled.img
                    src="/images/lightning.svg"
                    alt="lightning"
                    width="16px"
                    height="16px"
                  />
                ) : null}
                <WalletAddress textStyle="numbers.small" address={address} />
              </>
            ) : null}
            <Menu.Context>
              {(ctx) => (
                <Icon
                  boxSize="4"
                  color="inherit"
                  transform={ctx.open ? 'rotate(180deg)' : ''}
                  transition="transform 0.2s ease"
                >
                  <ArrowDown />
                </Icon>
              )}
            </Menu.Context>
          </Box>
        </UserWalletMenu>

        <Button
          display={{ base: address ? 'none' : 'flex', desktop: 'flex' }}
          variant="solid"
          colorPalette="buttonPrimary"
          gap={2}
          h="8"
          px={2}
          py={1}
          position="relative"
          data-cy="WalletButton-Connected"
          data-cy-value={chainId}
        >
          <NotificationDot position="absolute" top={1} right={1} />
          <styled.img src={getChainInfo(chainId).icon} boxSize="4" />
          <Divider orientation="vertical" />
          <styled.img src={currentConnector?.icon} boxSize="4" />
        </Button>
      </Box>
    </>
  );
};
